// Combined Payment Dialog with Numeric Keypad
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Banknote, CreditCard, Delete, Check, X } from 'lucide-react';

export function CombinedPaymentDialog({ 
  isOpen, 
  onClose, 
  total, 
  onConfirm 
}) {
  const [activeField, setActiveField] = useState('cash'); // 'cash' or 'card'
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCashAmount('');
      setCardAmount('');
      setActiveField('cash');
    }
  }, [isOpen]);

  // Auto-calculate remaining
  useEffect(() => {
    const cash = parseFloat(cashAmount) || 0;
    const remaining = total - cash;
    if (activeField === 'cash' && cash > 0 && remaining >= 0) {
      setCardAmount(remaining.toFixed(2));
    }
  }, [cashAmount, total, activeField]);

  useEffect(() => {
    const card = parseFloat(cardAmount) || 0;
    const remaining = total - card;
    if (activeField === 'card' && card > 0 && remaining >= 0) {
      setCashAmount(remaining.toFixed(2));
    }
  }, [cardAmount, total, activeField]);

  const handleKeyPress = (key) => {
    const currentValue = activeField === 'cash' ? cashAmount : cardAmount;
    const setValue = activeField === 'cash' ? setCashAmount : setCardAmount;

    if (key === 'delete') {
      setValue(currentValue.slice(0, -1));
    } else if (key === 'clear') {
      setValue('');
    } else if (key === '.') {
      if (!currentValue.includes('.')) {
        setValue(currentValue + '.');
      }
    } else {
      // Check decimal places
      if (currentValue.includes('.')) {
        const decimals = currentValue.split('.')[1];
        if (decimals && decimals.length >= 2) return;
      }
      setValue(currentValue + key);
    }
  };

  const handleConfirm = () => {
    const cash = parseFloat(cashAmount) || 0;
    const card = parseFloat(cardAmount) || 0;
    
    if (Math.abs((cash + card) - total) < 0.01) {
      onConfirm(cash, card);
    }
  };

  const cash = parseFloat(cashAmount) || 0;
  const card = parseFloat(cardAmount) || 0;
  const sumTotal = cash + card;
  const isValid = Math.abs(sumTotal - total) < 0.01;
  const difference = total - sumTotal;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-zinc-800">
          <DialogTitle className="font-heading text-xl text-white text-center">
            Pago Combinado
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-80px)]">
          <div className="p-4 space-y-4">
          {/* Total to pay */}
          <div className="text-center py-2">
            <p className="text-zinc-500 text-sm uppercase tracking-wide">Total a Pagar</p>
            <p className="font-heading text-3xl text-gold">${total.toFixed(2)}</p>
          </div>

          {/* Amount inputs */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cash */}
            <button
              onClick={() => setActiveField('cash')}
              className={`p-4 rounded-lg border-2 transition-all ${
                activeField === 'cash'
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-zinc-700 bg-zinc-800/50'
              }`}
              data-testid="combined-cash-field"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Banknote className={`w-5 h-5 ${activeField === 'cash' ? 'text-emerald-400' : 'text-zinc-500'}`} />
                <span className={`text-sm font-medium ${activeField === 'cash' ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  Efectivo
                </span>
              </div>
              <div className={`text-2xl font-mono font-bold ${activeField === 'cash' ? 'text-white' : 'text-zinc-400'}`}>
                ${cashAmount || '0.00'}
              </div>
            </button>

            {/* Card */}
            <button
              onClick={() => setActiveField('card')}
              className={`p-4 rounded-lg border-2 transition-all ${
                activeField === 'card'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-zinc-700 bg-zinc-800/50'
              }`}
              data-testid="combined-card-field"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <CreditCard className={`w-5 h-5 ${activeField === 'card' ? 'text-blue-400' : 'text-zinc-500'}`} />
                <span className={`text-sm font-medium ${activeField === 'card' ? 'text-blue-400' : 'text-zinc-500'}`}>
                  Tarjeta
                </span>
              </div>
              <div className={`text-2xl font-mono font-bold ${activeField === 'card' ? 'text-white' : 'text-zinc-400'}`}>
                ${cardAmount || '0.00'}
              </div>
            </button>
          </div>

          {/* Difference indicator */}
          <div className={`text-center py-2 rounded-lg ${
            isValid 
              ? 'bg-green-500/10 text-green-400' 
              : difference > 0 
                ? 'bg-yellow-500/10 text-yellow-400'
                : 'bg-red-500/10 text-red-400'
          }`}>
            {isValid ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Montos correctos
              </span>
            ) : difference > 0 ? (
              <span>Faltan: ${difference.toFixed(2)}</span>
            ) : (
              <span>Excede: ${Math.abs(difference).toFixed(2)}</span>
            )}
          </div>

          {/* Numeric Keypad */}
          <div className="grid grid-cols-3 gap-2">
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'delete'].map((key) => (
              <button
                key={key}
                onClick={() => handleKeyPress(key)}
                className={`h-14 rounded-lg font-mono text-xl font-bold transition-all active:scale-95 ${
                  key === 'delete'
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
                data-testid={`keypad-${key}`}
              >
                {key === 'delete' ? <Delete className="w-6 h-6 mx-auto" /> : key}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="combined-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!isValid}
              className="h-12 bg-gold text-black hover:bg-gold-dark disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="combined-confirm-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmar
            </Button>
          </div>
        </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
